import React, { ReactNode } from 'react';
import ProjectSettingsGeneral from './ProjectSettingsGeneral';
import ProjectSettingsPricingFields from './ProjectSettingsPricingFields/ProjectSettingsPricingFields';
import ProjectSettingsFeatures from './ProjectSettingsFeatures';
import ProjectSettingsFees from './ProjectSettingsFees';
import ProjectSettingsTerms from './ProjectSettingsTerms';
import ProjectSettingsDeadlineAlerts from './ProjectSettingsDeadlineAlerts';
import ProjectSettingsTags from './ProjectSettingsTags';
import ProjectSettingsDeadlines from './ProjectSettingsDeadlines';
import { Trans } from '@lingui/react/macro';
import { useParamReplace } from '@/utils/hooks';
import { CustomRedirect } from '@/components/navigation';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { Sticky, Tabs, useBreakpoint } from '@ygtechnologies/rubus-ui';
import theme from './ProjectSettings.module.scss';
import ProjectSettingsConstructionPhases from './ProjectSettingsBuildingSteps/ProjectSettingsConstructionPhases';
import { ProjectSettingsUnitVariables } from './ProjectSettingsUnitVariables';
import ProjectWhiteLabel from '../ProjectWhiteLabelSettings';

enum TabId {
    General = 'general',
    Features = 'features',
    Fees = 'fees',
    PricingVariables = 'pricing-variables',
    Deadlines = 'deadlines',
    DeadlineAlerts = 'deadline-alerts',
    Terms = 'terms',
    Tags = 'tags',
    Styles = 'styles',
    ConstructionPhases = 'construction-phases',
    UnitVariables = 'unit-variables',
    WhiteLabel = 'white-label',
}

interface TabDefinition {
    id: TabId;
    caption(): ReactNode;
    render(props: { projectId: number }): ReactNode;
    adminOnly?: boolean;
}

const TabDefinitions: TabDefinition[] = [
    {
        id: TabId.General,
        caption: () => <Trans>General</Trans>,
        render: ({ projectId }) => <ProjectSettingsGeneral projectId={projectId} />,
    },
    {
        id: TabId.Features,
        caption: () => <Trans>Features</Trans>,
        render: ({ projectId }) => <ProjectSettingsFeatures projectId={projectId} />,
    },
    {
        id: TabId.ConstructionPhases,
        caption: () => <Trans>Construction phases</Trans>,
        render: ({ projectId }) => <ProjectSettingsConstructionPhases projectId={projectId} />,
    },
    {
        id: TabId.Fees,
        caption: () => <Trans>Fees</Trans>,
        render: ({ projectId }) => <ProjectSettingsFees projectId={projectId} />,
    },
    {
        id: TabId.PricingVariables,
        caption: () => <Trans>Pricing variables</Trans>,
        render: ({ projectId }) => <ProjectSettingsPricingFields projectId={projectId} />,
    },
    {
        id: TabId.Deadlines,
        caption: () => <Trans>Deadlines</Trans>,
        render: ({ projectId }) => <ProjectSettingsDeadlines projectId={projectId} />,
    },
    {
        id: TabId.DeadlineAlerts,
        caption: () => <Trans>Deadline alerts</Trans>,
        render: ({ projectId }) => <ProjectSettingsDeadlineAlerts projectId={projectId} />,
    },
    {
        id: TabId.Terms,
        caption: () => <Trans>Terms and conditions &amp; privacy policy</Trans>,
        render: ({ projectId }) => <ProjectSettingsTerms projectId={projectId} />,
    },
    {
        id: TabId.Tags,
        caption: () => <Trans>Tags</Trans>,
        render: ({ projectId }) => <ProjectSettingsTags projectId={projectId} />,
    },
    {
        id: TabId.UnitVariables,
        caption: () => <Trans>Unit variables</Trans>,
        render: ({ projectId }) => <ProjectSettingsUnitVariables projectId={projectId} />,
    },
    {
        id: TabId.WhiteLabel,
        caption: () => <Trans>White labeling</Trans>,
        render: ({ projectId }) => <ProjectWhiteLabel projectId={projectId} />,
        adminOnly: true,
    },
];

type ProjectSettingsProps = {
    projectId: number;
};

type UrlParams = {
    settingsTab: TabId;
};

const ProjectSettings: React.FC<ProjectSettingsProps> = ({ projectId }) => {
    const { settingsTab: selectedTabId } = useParams<UrlParams>();
    const replaceParams = useParamReplace<UrlParams>();
    const isAdministrator = useSelector((state: IState) => !!state.global.activeProject?.permissions.isAdministrator);
    // Same breakpoint Tabs' collapseToDropdownOnMobile uses, so the dropdown collapse
    // and the sticky disable flip on the same event.
    const isMobile = useBreakpoint().down('md');

    const visibleTabs = TabDefinitions.filter((x) => !x.adminOnly || isAdministrator);

    const selectedTabDefinition = visibleTabs.find((x) => x.id === selectedTabId);
    if (!selectedTabDefinition) {
        return <CustomRedirect to={replaceParams({ settingsTab: TabId.General })} />;
    }

    return (
        <div className={theme.layout}>
            {/* 16px = the tab strip's margin-bottom, kept as a gap when the sidebar sticks. */}
            <Sticky className={theme.sidebar} additionalOffset={16} enabled={!isMobile}>
                <Tabs
                    activeId={selectedTabId}
                    orientation="vertical"
                    variant="pill"
                    align="stretch"
                    collapseToDropdownOnMobile
                    className={theme.tabs}>
                    {visibleTabs.map((x) => (
                        <Tabs.Tab
                            key={x.id}
                            id={x.id}
                            as={Link}
                            to={replaceParams({ settingsTab: x.id })}
                            label={x.caption()}
                        />
                    ))}
                </Tabs>
            </Sticky>
            <div className={theme.content}>{selectedTabDefinition.render({ projectId })}</div>
        </div>
    );
};

export default ProjectSettings;
